import React, { useEffect, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Typography, useTheme, Box, Divider } from '@mui/material';
import { tokens } from '../../theme';
import { formattedDate } from '../../Utils/Functions';
import RoleChip from '../../SystemDesign/RoleChip';
import MuiDialog from '../../SystemDesign/MuiDialog';
import InfoIcon from '@mui/icons-material/Info';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { DialogSection, DialogSectionLabel, DialogSectionValue } from '../../SystemDesign/Styles';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useGetUserQuery } from '../../services/SkillsManagement';

const ProfilEmployee = ({ row }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Support various row shapes
  const user = (row && (row.original?.user || row.original)) || row || {};

  // Fetch user details if evaluations missing
  const userId = user?.id || user?.userId;
  const shouldFetchDetails = !Array.isArray(user?.evaluations) || user.evaluations.length === 0;
  const { data: userDetails, isFetching: isFetchingUser } = useGetUserQuery(
    { userId: Number(userId), type: 'user' },
    { skip: !userId || !shouldFetchDetails }
  );

  // Merge fetched details
  const fullUser = useMemo(() => {
    const details = userDetails?.data || userDetails;
    return shouldFetchDetails && details ? { ...user, ...details } : user;
  }, [user, userDetails, shouldFetchDetails]);

  // Build assigned skills
  const assignedSkills = useMemo(() => {
    const evals = (Array.isArray(fullUser?.evaluations) && fullUser.evaluations) || [];
    const items = evals
      .map((ev) => {
        const skill = ev?.skill || {};
        const code = skill?.skill_code || '';
        const name = skill?.name || '';
        if (!code && !name) return null;
        return { code: String(code), name: String(name), score: ev?.score, level: ev?.level };
      })
      .filter(Boolean);
    const seen = new Set();
    const unique = items.filter((it) => {
      const key = it.code || it.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [fullUser?.evaluations]);

  useEffect(() => {
    // optional side effect
  }, [user.profile?.profilePic]);

  // Derive position fields with fallbacks
  const positionName = fullUser?.poste?.name
    || fullUser?.posteName
    || fullUser?.position
    || fullUser?.jobTitle
    || null;
  const departmentName = fullUser?.department?.name
    || fullUser?.departmentName
    || fullUser?.unit?.name
    || fullUser?.unitName
    || null;
  const positionAssignedAt = fullUser?.posteAssignedAt || fullUser?.assignedAt || null;

  const sections = [
    {
      id: 'profile',
      title: 'Bio Information',
      icon: <ContactsOutlinedIcon />,
      content: [
        { label: 'First Name', value: user.profile?.firstName },
        { label: 'Last Name', value: user.profile?.lastName },
        { label: 'Gender', value: user.profile?.gender },
        { label: 'Phone', value: user.profile?.phone },
        { label: 'Birthday', value: user.profile?.birthday ? formattedDate(new Date(user.profile.birthday)) : '-' },
        { label: 'Bio', value: user.profile?.bio, multiline: true }
      ]
    },
    {
      id: 'personal',
      title: 'Account Details',
      icon: <PersonOutlineRoundedIcon />,
      content: [
        { label: 'Username', value: user.username },
        { label: 'Email', value: user.email },
      ]
    },
    {
      id: 'account',
      title: 'System Information',
      icon: <ManageAccountsOutlinedIcon />,
      content: [
        { label: 'Role', value: null, chip: <RoleChip role={user.roleType} /> },
        { label: 'Created At', value: user.createdAt ? formattedDate(new Date(user.createdAt)) : '-' },
        { label: 'Updated At', value: user.updatedAt ? formattedDate(new Date(user.updatedAt)) : '-' }
      ]
    },
    {
      id: 'position',
      title: 'Assigned Position',
      icon: <WorkOutlineOutlinedIcon />,
      content: [
        { label: 'Position', value: positionName },
      ]
    },
    {
      id: 'skills',
      title: 'Skills',
      icon: <WorkspacePremiumOutlinedIcon />,
      content: [
        {
          type: 'custom',
          component: (
            <Box sx={{ mt: 1 }}>
              {isFetchingUser ? (
                <Typography variant="body2" color="text.secondary">Loading skills...</Typography>
              ) : assignedSkills && assignedSkills.length > 0 ? (
                <Box>
                  {assignedSkills.map((s, idx) => (
                    <Typography key={s.code || s.name} variant="body2" sx={{ display: 'block', mb: 0.25 }}>
                      {`${idx + 1}. ${s.code || '-'} — ${s.name || '-'}`}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No skills assigned.</Typography>
              )}
            </Box>
          ),
        },
      ],
    },
  ];

  // Reusable components
  const DetailItem = ({ label, value, chip, type, multiline, icon, component }) => {
    if (type === 'custom' && component) {
      return component;
    }

    return (
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          gap: 0.5,
        }}
      >
        <Typography sx={DialogSectionLabel(colors)}>{label} :</Typography>
        {chip ? (
          <Box sx={{ '& .MuiChip-root': { transition: 'all 0.2s', '&:hover': { opacity: 0.8 } } }}>
            {chip}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              sx={{
                ...DialogSectionValue(colors),
                ...(multiline && {
                  whiteSpace: 'pre-wrap',
                  maxWidth: '400px',
                }),
              }}
            >
              {value || '-'}
            </Typography>
            {icon}
          </Box>
        )}
      </Box>
    );
  };

  const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
      <Typography sx={{ ...DialogSection(colors), mr: 1 }}>
        {icon}{title}
      </Typography>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  );

  const dialogContent = (
    <Box sx={{ width: '100%' }}>
      {sections.map((section, idx) => (
        <Box key={section.id} sx={{ width: '100%', mb: idx === sections.length - 1 ? 0 : 2 }}>
          <SectionHeader title={section.title} icon={section.icon} />
          {section.content.map((item, index) => (
            <DetailItem key={item.id || index} {...item} />
          ))}
        </Box>
      ))}
    </Box>
  );

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <VisibilityIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>View Bio</ListItemText>
    </MenuItem>
  );

  return (
    <MuiDialog
      icon={<InfoIcon />}
      triggerButton={triggerButton}
      title="Employee Bio"
      subtitle={`Viewing Bio for ${user.username}`}
      content={dialogContent}
      existAction={false}
      maxWidth="md"
    />
  );
};

export default ProfilEmployee;